import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Input,
  Progress,
  Result,
  Spin,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleFilled,
  LockOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { PublicClarification } from '../../../private/modules/job-application/models/clarification.model';
import { ClarificationService } from '../../../private/modules/job-application/services/clarification.service';
import './ClarificationResponsePage.css';

const ClarificationResponsePage: React.FC = () => {
  const { token = '' } = useParams();
  const [data, setData] = useState<PublicClarification | null>(null);
  const [phase, setPhase] = useState<'loading' | 'answer' | 'done' | 'gone'>(
    'loading'
  );
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const answeredCount =
    data?.questions.filter((question) => answers[question.id]?.trim()).length ??
    0;
  const questionCount = data?.questions.length ?? 0;
  const complete = questionCount > 0 && answeredCount === questionCount;

  useEffect(() => {
    (async () => {
      try {
        setData(await ClarificationService.publicView(token));
        setPhase('answer');
      } catch {
        setPhase('gone');
      }
    })();
  }, [token]);

  const submit = async () => {
    if (!data?.questions.every((q) => answers[q.id]?.trim())) {
      message.error('Please answer every question.');
      return;
    }
    setBusy(true);
    try {
      await ClarificationService.submit(
        token,
        data.questions.map((q) => ({ questionId: q.id, answer: answers[q.id] }))
      );
      setPhase('done');
    } catch {
      message.error('Could not submit your answers.');
    } finally {
      setBusy(false);
    }
  };

  if (phase === 'loading')
    return (
      <main className="clarification-public">
        <Spin size="large" tip="Loading secure request…" />
      </main>
    );
  if (phase === 'gone')
    return (
      <main className="clarification-public">
        <Result
          status="warning"
          title="This clarification link is no longer available"
          subTitle="It may have expired, been answered, or been withdrawn."
        />
      </main>
    );
  if (phase === 'done')
    return (
      <main className="clarification-public clarification-public--complete">
        <section className="clarification-complete">
          <div className="clarification-complete__icon">
            <CheckCircleFilled />
          </div>
          <span className="clarification-complete__eyebrow">ALL DONE</span>
          <Typography.Title level={2}>Answers submitted</Typography.Title>
          <Typography.Paragraph>
            Thank you. Your responses were sent successfully and are ready for
            the recruiter to review.
          </Typography.Paragraph>
          <div className="clarification-complete__close">
            <CheckCircleFilled />
            <span>
              <strong>No further action is needed</strong>
              You can safely close this window.
            </span>
          </div>
          <small>ITJobOpportunities · Secure candidate response</small>
        </section>
      </main>
    );

  return (
    <main className="clarification-public">
      <Card className="clarification-public__card">
        <header>
          <span>
            <MessageOutlined />
          </span>
          <div>
            <small>{data?.companyName}</small>
            <Typography.Title level={2}>
              Help us complete your application
            </Typography.Title>
            <Typography.Paragraph>{data?.jobTitle}</Typography.Paragraph>
          </div>
        </header>
        <div className="clarification-public__trust">
          <LockOutlined />
          <span>
            <strong>Private and secure</strong>
            This single-use link is only for questions about this application.
          </span>
        </div>
        {phase === 'answer' ? (
          <section>
            <div className="clarification-public__section-heading">
              <div>
                <Typography.Title level={4}>Your questions</Typography.Title>
                <Typography.Paragraph type="secondary">
                  Share concise, accurate details. Your answers go directly to
                  the recruiter.
                </Typography.Paragraph>
              </div>
              <strong>
                {answeredCount} of {questionCount} answered
              </strong>
            </div>
            <Progress
              percent={
                questionCount
                  ? Math.round((answeredCount / questionCount) * 100)
                  : 0
              }
              showInfo={false}
              strokeColor="#0874ad"
              trailColor="#e5edf4"
              size="small"
            />
            <div className="clarification-public__questions">
              {data?.questions.map((q, i) => (
                <article key={q.id}>
                  <span>{i + 1}</span>
                  <div>
                    <strong>{q.question}</strong>
                    <Input.TextArea
                      autoSize={{ minRows: 4, maxRows: 8 }}
                      maxLength={4000}
                      showCount
                      placeholder="Type your answer here…"
                      value={answers[q.id] || ''}
                      onChange={(e) =>
                        setAnswers((v) => ({ ...v, [q.id]: e.target.value }))
                      }
                    />
                  </div>
                </article>
              ))}
            </div>
            <Button
              type="primary"
              size="large"
              block
              icon={<CheckCircleFilled />}
              loading={busy}
              disabled={!complete}
              onClick={() => void submit()}
            >
              Submit{' '}
              {questionCount === 1 ? 'answer' : `${questionCount} answers`}
            </Button>
            <p className="clarification-public__final-note">
              Review your responses before submitting. Answers cannot be edited
              afterward.
            </p>
          </section>
        ) : null}
        <footer>
          Secure response · Link expires{' '}
          {data?.expiresAt ? new Date(data.expiresAt).toLocaleDateString() : ''}
        </footer>
      </Card>
    </main>
  );
};
export default ClarificationResponsePage;
